// Opens a generated report in a new tab as a printable document.
//
// The tab gets a small toolbar (hidden when printing) with "Download PDF" and
// "Print / Save as PDF". The print dialog only opens when the user clicks the
// print button. The download button renders the report to a PDF file via
// html2pdf (html2canvas + jsPDF), loaded into the tab from our own bundle.
import html2pdfBundleUrl from "html2pdf.js/dist/html2pdf.bundle.min.js?url";

interface PrintableDocumentOptions {
  // Full HTML document (or a body fragment — it gets wrapped).
  html: string;
  // Browser tab title.
  title: string;
  // Base name for the downloaded file, without extension.
  fileName: string;
}

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

// Safe inside a <script> block in an HTML document.
const toScriptString = (value: string) =>
  JSON.stringify(value).replace(/</g, "\\u003c");

const TOOLBAR_STYLE = `
<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Rubik:wght@400;500;600&display=swap">
<style id="ame-export-chrome">
  #ame-export-toolbar {
    position: sticky;
    top: 0;
    z-index: 99999;
    display: flex;
    gap: 8px;
    justify-content: flex-end;
    align-items: center;
    padding: 10px 16px;
    background: #ffffff;
    border-bottom: 1px solid #e5e7eb;
    font-family: "Rubik", sans-serif;
  }
  #ame-export-toolbar button {
    font-family: inherit;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    font-size: 14px;
    font-weight: 600;
    color: #374151;
    background: #ffffff;
    border: 1px solid #d0d5dd;
    border-radius: 8px;
    cursor: pointer;
  }
  #ame-export-toolbar button:hover { background: #f3f4f6; }
  #ame-export-toolbar button[disabled] { opacity: 0.6; cursor: default; }
  #ame-export-toolbar button.primary {
    color: #ffffff;
    background: #2563eb;
    border-color: #2563eb;
  }
  #ame-export-toolbar button.primary:hover { background: #1d4ed8; }
  @media print {
    #ame-export-toolbar { display: none !important; }
  }
</style>`;

const buildToolbar = (fileName: string) => `
<div id="ame-export-toolbar">
  <button type="button" id="ame-download-btn">⬇ Download PDF</button>
  <button type="button" class="primary" id="ame-print-btn">🖨 Print / Save as PDF</button>
</div>
<script id="ame-export-script">
(function () {
  var fileName = ${toScriptString(fileName)};

  document.getElementById("ame-print-btn").addEventListener("click", function () {
    window.print();
  });

  // Renders the report (without the toolbar) to a PDF file and downloads it.
  document.getElementById("ame-download-btn").addEventListener("click", function () {
    var btn = document.getElementById("ame-download-btn");
    if (btn.disabled) return;
    if (typeof html2pdf === "undefined") {
      alert("The PDF generator hasn't finished loading. Please try again, or use Print / Save as PDF.");
      return;
    }
    var toolbar = document.getElementById("ame-export-toolbar");
    var originalLabel = btn.textContent;
    btn.disabled = true;
    btn.textContent = "Preparing PDF…";
    toolbar.style.display = "none";
    var restore = function () {
      toolbar.style.display = "";
      btn.disabled = false;
      btn.textContent = originalLabel;
    };
    html2pdf()
      .set({
        margin: 10,
        filename: fileName + ".pdf",
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: { mode: ["css", "legacy"] },
      })
      .from(document.body)
      .save()
      .catch(function () {
        alert("Could not generate the PDF. Please use Print / Save as PDF instead.");
      })
      .then(restore, restore);
  });
})();
</script>`;

export const openPrintableDocument = ({
  html,
  title,
  fileName,
}: PrintableDocumentOptions): boolean => {
  const newWindow = window.open("", "_blank");
  if (!newWindow) {
    alert("Please allow popups for this site to export your report.");
    return false;
  }

  // The new tab is about:blank, so relative asset paths (the AME logo) need a
  // base URL to resolve. The html2pdf bundle is served from our own origin.
  const head = `<base href="${window.location.origin}/"><title>${escapeHtml(
    title,
  )}</title>${TOOLBAR_STYLE}<script src="${html2pdfBundleUrl}"></script>`;
  const toolbar = buildToolbar(fileName);

  let document_ = html;
  if (/<\/head>/i.test(document_)) {
    // Full document — add to its existing head.
    document_ = document_.replace(/<\/head>/i, `${head}</head>`);
  } else if (/<body[^>]*>/i.test(document_)) {
    // Has a body but no head — insert one before it.
    document_ = document_.replace(
      /<body[^>]*>/i,
      (match) => `<head>${head}</head>${match}`,
    );
  } else {
    // Bare fragment — wrap it.
    document_ = `<!DOCTYPE html><html><head>${head}</head><body>${document_}</body></html>`;
  }

  // Toolbar goes at the very top of the body.
  document_ = document_.replace(/<body[^>]*>/i, (match) => match + toolbar);

  newWindow.document.write(document_);
  newWindow.document.close();
  return true;
};

export default openPrintableDocument;
